/**
 * Email/password login with react-hook-form + zod; seeds JWTs and redirects to prior route.
 *
 * Where: `/login` public route.
 * Uses: `useAuth` login mutation, MUI card layout.
 * Behavior: shows validation errors; reads `location.state.from` for post-auth redirect when present.
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Avatar,
    Alert,
    InputAdornment,
    FormControlLabel,
    Checkbox,
    Link, IconButton,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import { useAuth } from '@/hooks/useAuth';

// Validation schema
const loginSchema = z.object({
  email: z.string().min(1, 'email is required'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const Login: React.FC = () => {
  const { login, isLoginLoading, loginError } = useAuth();
  const [showPassword, setShowPassword] = React.useState(false);
  const [isLinkedInLoading, setIsLinkedInLoading] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = (data: LoginFormData) => {
    login({ email: data.email, password: data.password });
  };

  const handleLinkedInConnect = async () => {
    setIsLinkedInLoading(true);
    try {
      const reconnectUrl = `${window.location.origin}/reconnect-linkedin`;
      window.location.href = reconnectUrl;
    } finally {
      setIsLinkedInLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: 2,
        py: 8,
      }}
    >
      <Box sx={{ maxWidth: 400, width: '100%' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar
            sx={{
              mx: 'auto',
              mb: 3,
              width: 64,
              height: 64,
              bgcolor: 'primary.main',
              boxShadow: 2,
            }}
          >
            <BusinessIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Unblockly
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to your workspace
          </Typography>
        </Box>

        {/* Login Form */}
        <Card sx={{ boxShadow: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* email Field */}
              <TextField
                {...register('email')}
                fullWidth
                label="email"
                variant="outlined"
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {/* Password Field */}
              <TextField
                {...register('password')}
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        sx={{ minWidth: 'auto', p: 1 }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {/* Remember Me & Forgot Password */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <FormControlLabel
                  control={<Checkbox {...register('rememberMe')} />}
                  label="Remember me"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                />
                <Link href="#" variant="body2" color="primary">
                  Forgot password?
                </Link>
              </Box>

              {/* Global Error */}
              {loginError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {loginError.message || 'Invalid email or password'}
                </Alert>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoginLoading}
                sx={{
                  py: 1.5,
                  fontWeight: 500,
                  fontSize: '1rem',
                }}
              >
                {isLoginLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* LinkedIn Connection */}
        <Card sx={{ boxShadow: 2, mt: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Connect LinkedIn Account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect your LinkedIn account to enable messaging features
              </Typography>
              <Button
                variant="outlined"
                size="large"
                startIcon={<LinkedInIcon />}
                onClick={handleLinkedInConnect}
                disabled={isLinkedInLoading}
                fullWidth
                sx={{
                  py: 1.5,
                  fontWeight: 500,
                  fontSize: '1rem',
                  borderColor: '#0077b5',
                  color: '#0077b5',
                  '&:hover': {
                    borderColor: '#005885',
                    backgroundColor: 'rgba(0, 119, 181, 0.04)',
                  },
                }}
              >
                {isLinkedInLoading ? 'Connecting...' : 'Connect LinkedIn Account'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Don't have an account?{' '}
            <Link href="#" color="primary">
              Contact administrator
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
